import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  ModalFooter,
  Label,
} from "reactstrap";
// core components
import Header from "components/Headers/Header";
import {
  API_BASE,
  API_GETZIPCODE,
  API_GETORG,
  API_EDITORG,
} from "../../config/api";
import { api } from "../../config/middleware";
import { useSessionStore } from "../../stores/sessionStore";
import Swal from "sweetalert2";
import LoadingSpinner from "components/LoadingSpinner";

const AdminOrganization = () => {
  const navigate = useNavigate();

  const tnId = useSessionStore((s) => s.profile?.tnId) ?? "";
  const orgId = useSessionStore((s) => s.profile?.orgId) ?? "";

  const toPublicFileUrl = (rel) => {
    if (!rel) return "";
    const clean = String(rel)
      .replace(/^uploads[\\/]/, "")
      .replace(/\\/g, "/");
    return `${API_BASE.replace(/\/+$/, "")}/files/${clean}`;
  };

  const [isLoading, setIsLoading] = useState(false);

  // โหลดข้อมูลองค์กรมาใส่ฟอร์ม (ทุกฟีลด์)
  useEffect(() => {
    if (!orgId) return;

    (async () => {
      setIsLoading(true);
      try {
        const res = await api.get(API_GETORG, { params: { ORGID: orgId } });
        const d = res?.data?.data;
        if (!d) return;

        setForm((s) => ({
          ...s,
          orgNameTH: d.orgNameTH ?? "",
          orgPrefix: d.orgPrefix ?? "",
          cmAddr: d.orgAddr ?? "",
          cmAddr2: d.orgAddr2 ?? "",
          postalCode: d.orgZipcode ?? "",
          subdistrict: d.orgSubDistrict ?? "",
          district: d.orgDistrict ?? "",
          province: d.orgProvince ?? "",
          cmMobile: d.orgMobile ?? "",
          cmEmail: d.orgEmail ?? "",
          billNameTh: d.orgBillNameTH ?? "",
          billNameEn: d.orgBillNameEN ?? "",
          taxId: d.orgTax ?? "",
          billAddr: d.orgBillAddr ?? "",
          billAddr2: d.orgBillAddr2 ?? "",
          billZipcode: d.orgBillZipcode ?? "",
          billSubdistrict: d.orgBillSubDistrict ?? "",
          billDistrict: d.orgBillDistrict ?? "",
          billProvince: d.orgBillProvince ?? "",
          billMobile: d.orgBillMobile ?? "",
          billEmail: d.orgBillEmail ?? "",
        }));

        if (d.orgLogo) setLogoPreview(toPublicFileUrl(d.orgLogo));
      } catch (err) {
        console.error("GETORG error:", err);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [orgId]);

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const objectUrl = URL.createObjectURL(file);
    setLogoPreview(objectUrl);
  };

  useEffect(() => {
    return () => {
      if (logoPreview && logoPreview.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const [form, setForm] = useState({
    // ข้อมูลพื้นฐานองค์กร
    orgNameTH: "",
    orgPrefix: "",
    cmAddr: "",
    cmAddr2: "",
    postalCode: "",
    subdistrict: "",
    district: "",
    province: "",
    cmMobile: "",
    cmEmail: "",

    // ข้อมูลใบกำกับภาษี
    billNameTh: "",
    billNameEn: "",
    taxId: "",
    billAddr: "",
    billAddr2: "",
    billZipcode: "",
    billSubdistrict: "",
    billDistrict: "",
    billProvince: "",
    billMobile: "",
    billEmail: "",
  });

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const logoInputRef = useRef(null);

  // --- ZIP search states ---
  const [zipQuery, setZipQuery] = useState("");
  const [zipList, setZipList] = useState([]);
  const [zipOpen, setZipOpen] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);
  const zipBoxRef = useRef(null);

  useEffect(() => {
    if (zipQuery === "") {
      setZipList([]);
      setZipOpen(false);
      return;
    }
    const delay = setTimeout(() => {
      setZipLoading(true);
      const current = zipQuery;
      api
        .get(API_GETZIPCODE, { params: { zipcode: current } })
        .then((res) => {
          if (current !== zipQuery) return;
          const rows = res?.data?.data ?? [];
          setZipList(rows);
          setZipOpen(true);
        })
        .catch(() => {
          if (current !== zipQuery) return;
          setZipList([]);
          setZipOpen(false);
        })
        .finally(() => {
          if (current !== zipQuery) return;
          setZipLoading(false);
        });
    }, 250);
    return () => clearTimeout(delay);
  }, [zipQuery]);

  const onZipChange = (e) => {
    const v = e.target.value;
    setForm((s) => ({ ...s, postalCode: v }));
    setZipQuery(v);
  };

  const onPickZip = (item) => {
    setForm((s) => ({
      ...s,
      postalCode: String(item.zip_code ?? ""),
      subdistrict: item.subdistrict_name ?? "",
      district: item.district_name ?? "",
      province: item.province_name ?? "",
    }));
    setZipOpen(false);
    setZipList([]);
    setZipQuery("");
  };

  // --- Billing ZIP search states ---
  const [billZipQuery, setBillZipQuery] = useState("");
  const [billZipList, setBillZipList] = useState([]);
  const [billZipOpen, setBillZipOpen] = useState(false);
  const [billZipLoading, setBillZipLoading] = useState(false);
  const billZipBoxRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (billZipBoxRef.current && !billZipBoxRef.current.contains(e.target)) {
        setBillZipOpen(false);
      }
      if (zipBoxRef.current && !zipBoxRef.current.contains(e.target)) {
        setZipOpen(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  useEffect(() => {
    if (billZipQuery === "") {
      setBillZipList([]);
      setBillZipOpen(false);
      return;
    }
    const delay = setTimeout(() => {
      setBillZipLoading(true);
      const current = billZipQuery;
      api
        .get(API_GETZIPCODE, { params: { zipcode: current } })
        .then((res) => {
          if (current !== billZipQuery) return;
          const rows = res?.data?.data ?? [];
          setBillZipList(rows);
          setBillZipOpen(true);
        })
        .catch(() => {
          if (current !== billZipQuery) return;
          setBillZipList([]);
          setBillZipOpen(false);
        })
        .finally(() => {
          if (current !== billZipQuery) return;
          setBillZipLoading(false);
        });
    }, 250);
    return () => clearTimeout(delay);
  }, [billZipQuery]);

  const onBillZipChange = (e) => {
    const v = e.target.value;
    setForm((s) => ({ ...s, billZipcode: v }));
    setBillZipQuery(v);
  };

  const onPickBillZip = (item) => {
    setForm((s) => ({
      ...s,
      billZipcode: String(item.zip_code ?? ""),
      billSubdistrict: item.subdistrict_name ?? "",
      billDistrict: item.district_name ?? "",
      billProvince: item.province_name ?? "",
    }));
    setBillZipOpen(false);
    setBillZipList([]);
    setBillZipQuery("");
  };

  // ---------- Submit ----------
  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    const isJuristic =
      Boolean(form.billNameTh?.trim()) && Boolean(form.taxId?.trim());
    const NV = (v) => {
      if (v === undefined) return null;
      const t = String(v ?? "").trim();
      return t === "" ? null : t;
    };

    const payload = {
      TNID: tnId,
      ORGID: orgId,
      ORGTYPE: isJuristic ? "นิติบุคคล" : "บุคคลธรรมดา",
      ORGNAMETH: NV(form.orgNameTH),
      ORGPREFIX: NV(form.orgPrefix),
      ORGADDR: NV(form.cmAddr),
      ORGADDR2: NV(form.cmAddr2),
      ORGSUBDISTRICT: NV(form.subdistrict),
      ORGDISTRICT: NV(form.district),
      ORGPROVINCE: NV(form.province),
      ORGZIPCODE: NV(form.postalCode),
      ORGMOBILE: NV(form.cmMobile),
      ORGEMAIL: NV(form.cmEmail),
      ORGBILLNAMETH: NV(form.billNameTh),
      ORGBILLNAMEEN: NV(form.billNameEn),
      ORGTAX: NV(form.taxId),
      ORGBILLADDR: NV(form.billAddr),
      ORGBILLADDR2: NV(form.billAddr2),
      ORGBILLSUBDISTRICT: NV(form.billSubdistrict),
      ORGBILLDISTRICT: NV(form.billDistrict),
      ORGBILLPROVINCE: NV(form.billProvince),
      ORGBILLZIPCODE: NV(form.billZipcode),
      ORGBILLMOBILE: NV(form.billMobile),
      ORGBILLEMAIL: NV(form.billEmail),

      ORGUPDATEDBY: tnId,
    };

    const fd = new FormData();
    if (logoFile) fd.append("ORGLOGO", logoFile);
    Object.entries(payload).forEach(([k, v]) =>
      fd.append(k, v == null ? "" : String(v)),
    );

    try {
      setIsLoading(true);

      const res = await api.post(API_EDITORG, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        transformRequest: [(data) => data],
      });

      const msg = res?.data?.message ?? "บันทึกสำเร็จ";
      await Swal.fire({
        icon: "success",
        title: "สำเร็จ",
        text: msg,
        confirmButtonText: "ตกลง",
      });

      navigate("/organization");
    } catch (err) {
      const msg =
        err?.response?.data?.message ??
        err?.message ??
        "บันทึกไม่สำเร็จ กรุณาลองใหม่หรือตรวจสอบข้อมูล";

      Swal.fire({
        icon: "error",
        title: "บันทึกไม่สำเร็จ",
        text: msg,
        confirmButtonText: "ตกลง",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Header />
      {/* Page content */}
      <Container className="mt--7" fluid>
        <Row>
          <Col xs="12">
            <Card className="bg-secondary shadow">
              {isLoading && (
                <LoadingSpinner fullPage />
              )}
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h6 className="mb-0">ข้อมูลองค์กร</h6>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleSubmit}>
                  <h6 className="heading-small text-muted mb-4">
                    ข้อมูลพื้นฐาน
                  </h6>
                  <div className="pl-lg-4">
                    <Row className="justify-content-center">
                      <Col lg="6" md="8" sm="10" className="mx-auto">
                        <FormGroup className="text-center">
                          <label
                            className="form-control-label d-block"
                            htmlFor="org-logo-input"
                          >
                            โลโก้ร้าน/องค์กร
                          </label>

                          <div className="d-flex align-items-center justify-content-center w-100 mb-3">
                            <div
                              className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center"
                              style={{
                                width: 100,
                                height: 100,
                                overflow: "hidden",
                                border: "1px solid #e9ecef",
                                cursor: "pointer",
                              }}
                              onClick={() => logoInputRef.current?.click()}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) =>
                                (e.key === "Enter" || e.key === " ") &&
                                logoInputRef.current?.click()
                              }
                              aria-label="อัปโหลดโลโก้องค์กร"
                              title="อัปโหลดโลโก้องค์กร"
                            >
                              {logoPreview ? (
                                <img
                                  alt="ตัวอย่างโลโก้องค์กร"
                                  src={logoPreview}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <span className="text-muted small">
                                  เลือกรูป
                                </span>
                              )}
                            </div>
                          </div>
                          <Input
                            innerRef={logoInputRef}
                            id="org-logo-input"
                            type="file"
                            accept="image/*"
                            onChange={onLogoChange}
                            className="d-none"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label required"
                            htmlFor="input-org-name"
                          >
                            ชื่อร้าน/องค์กร
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-org-name"
                            type="text"
                            name="orgNameTH"
                            value={form.orgNameTH}
                            onChange={onChange}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label required"
                            htmlFor="input-org-prefix"
                          >
                            ชื่อย่อร้าน/องค์กร
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-org-prefix"
                            type="text"
                            name="orgPrefix"
                            value={form.orgPrefix}
                            onChange={onChange}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label required"
                            htmlFor="input-cmaddr"
                          >
                            ที่อยู่
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-cmaddr"
                            type="text"
                            name="cmAddr"
                            value={form.cmAddr}
                            onChange={onChange}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-cmaddr2"
                          >
                            ที่อยู่เพิ่มเติม
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-cmaddr2"
                            type="text"
                            name="cmAddr2"
                            value={form.cmAddr2}
                            onChange={onChange}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="3">
                        <FormGroup
                          style={{ position: "relative" }}
                          innerRef={zipBoxRef}
                        >
                          <label
                            className="form-control-label required"
                            htmlFor="input-postal-code"
                          >
                            รหัสไปรษณีย์
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-postal-code"
                            type="text"
                            value={form.postalCode}
                            onChange={onZipChange}
                            onFocus={() => zipList.length && setZipOpen(true)}
                            autoComplete="off"
                          />

                          {zipOpen && (
                            <div
                              className="dropdown-menu show w-100"
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                zIndex: 1050,
                                maxHeight: 240,
                                overflowY: "auto",
                              }}
                            >
                              {zipLoading && (
                                <div className="dropdown-item text-muted small">
                                  กำลังค้นหา…
                                </div>
                              )}
                              {!zipLoading && zipList.length === 0 && (
                                <div className="dropdown-item text-muted small">
                                  ไม่พบข้อมูลรหัสนี้
                                </div>
                              )}
                              {!zipLoading &&
                                zipList.map((it, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    className="dropdown-item d-flex flex-column align-items-start"
                                    onClick={() => onPickZip(it)}
                                  >
                                    <div>
                                      <strong>{it.zip_code}</strong>
                                    </div>
                                    <div className="small text-muted">
                                      {it.subdistrict_name} • {it.district_name}{" "}
                                      • {it.province_name}
                                    </div>
                                  </button>
                                ))}
                            </div>
                          )}
                        </FormGroup>
                      </Col>

                      <Col lg="3">
                        <FormGroup>
                          <label
                            className="form-control-label required"
                            htmlFor="input-subdistrict"
                          >
                            ตำบล/แขวง
                          </label>
                          <Input
                            disabled
                            className="form-control-alternative"
                            id="input-subdistrict"
                            type="text"
                            value={form.subdistrict}
                            onChange={(e) =>
                              setForm((s) => ({
                                ...s,
                                subdistrict: e.target.value,
                              }))
                            }
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="3">
                        <FormGroup>
                          <label
                            className="form-control-label required"
                            htmlFor="input-district"
                          >
                            อำเภอ/เขต
                          </label>
                          <Input
                            disabled
                            className="form-control-alternative"
                            id="input-district"
                            type="text"
                            value={form.district}
                            onChange={(e) =>
                              setForm((s) => ({
                                ...s,
                                district: e.target.value,
                              }))
                            }
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="3">
                        <FormGroup>
                          <label
                            className="form-control-label required"
                            htmlFor="input-province"
                          >
                            จังหวัด
                          </label>
                          <Input
                            disabled
                            className="form-control-alternative"
                            id="input-province"
                            type="text"
                            value={form.province}
                            onChange={(e) =>
                              setForm((s) => ({
                                ...s,
                                province: e.target.value,
                              }))
                            }
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label required"
                            htmlFor="input-cmmobile"
                          >
                            เบอร์โทร
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-cmmobile"
                            type="text"
                            name="cmMobile"
                            value={form.cmMobile}
                            onChange={(e) => {
                              const onlyNums = e.target.value.replace(
                                /[^0-9]/g,
                                "",
                              );
                              setForm((s) => ({ ...s, cmMobile: onlyNums }));
                            }}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={10}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-cmemail"
                          >
                            Email
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-cmemail"
                            type="email"
                            name="cmEmail"
                            value={form.cmEmail}
                            onChange={onChange}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>

                  <hr className="my-4" />

                  <div className="pl-lg-3">
                    <h6 className="heading-small text-muted mb-4">
                      ข้อมูลที่แสดงในใบกำกับภาษี
                    </h6>

                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-bill-nameth"
                          >
                            ชื่อบริษัท (ภาษาไทย)
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-bill-nameth"
                            type="text"
                            name="billNameTh"
                            value={form.billNameTh}
                            onChange={onChange}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-bill-nameen"
                          >
                            ชื่อบริษัท (ภาษาอังกฤษ)
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-bill-nameen"
                            type="text"
                            name="billNameEn"
                            value={form.billNameEn}
                            onChange={onChange}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-taxid"
                          >
                            เลขประจำตัวผู้เสียภาษี
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-taxid"
                            type="text"
                            name="taxId"
                            value={form.taxId}
                            onChange={onChange}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-bill-addr"
                          >
                            ที่อยู่
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-bill-addr"
                            type="text"
                            name="billAddr"
                            value={form.billAddr}
                            onChange={onChange}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-bill-addr2"
                          >
                            ที่อยู่เพิ่มเติม
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-bill-addr2"
                            type="text"
                            name="billAddr2"
                            value={form.billAddr2}
                            onChange={onChange}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="3">
                        <FormGroup
                          style={{ position: "relative" }}
                          innerRef={billZipBoxRef}
                        >
                          <label
                            className="form-control-label"
                            htmlFor="input-bill-zipcode"
                          >
                            รหัสไปรษณีย์
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-bill-zipcode"
                            type="text"
                            value={form.billZipcode}
                            onChange={onBillZipChange}
                            onFocus={() =>
                              billZipList.length && setBillZipOpen(true)
                            }
                            autoComplete="new-password"
                            name="fakeBillZipcode"
                            inputMode="numeric"
                            pattern="[0-9]*"
                          />

                          {billZipOpen && (
                            <div
                              className="dropdown-menu show w-100"
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: 0,
                                zIndex: 1050,
                                maxHeight: 240,
                                overflowY: "auto",
                              }}
                            >
                              {billZipLoading && (
                                <div className="dropdown-item text-muted small">
                                  กำลังค้นหา…
                                </div>
                              )}
                              {!billZipLoading && billZipList.length === 0 && (
                                <div className="dropdown-item text-muted small">
                                  ไม่พบข้อมูลรหัสนี้
                                </div>
                              )}
                              {!billZipLoading &&
                                billZipList.map((it, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    className="dropdown-item d-flex flex-column align-items-start"
                                    onClick={() => onPickBillZip(it)}
                                  >
                                    <div>
                                      <strong>{it.zip_code}</strong>
                                    </div>
                                    <div className="small text-muted">
                                      {it.subdistrict_name} • {it.district_name}{" "}
                                      • {it.province_name}
                                    </div>
                                  </button>
                                ))}
                            </div>
                          )}
                        </FormGroup>
                      </Col>

                      <Col lg="3">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-bill-subdistrict"
                          >
                            ตำบล/แขวง
                          </label>
                          <Input
                            disabled
                            className="form-control-alternative"
                            id="input-bill-subdistrict"
                            type="text"
                            value={form.billSubdistrict}
                            onChange={(e) =>
                              setForm((s) => ({
                                ...s,
                                billSubdistrict: e.target.value,
                              }))
                            }
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="3">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-bill-district"
                          >
                            อำเภอ/เขต
                          </label>
                          <Input
                            disabled
                            className="form-control-alternative"
                            id="input-bill-district"
                            type="text"
                            value={form.billDistrict}
                            onChange={(e) =>
                              setForm((s) => ({
                                ...s,
                                billDistrict: e.target.value,
                              }))
                            }
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="3">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-bill-province"
                          >
                            จังหวัด
                          </label>
                          <Input
                            disabled
                            className="form-control-alternative"
                            id="input-bill-province"
                            type="text"
                            value={form.billProvince}
                            onChange={(e) =>
                              setForm((s) => ({
                                ...s,
                                billProvince: e.target.value,
                              }))
                            }
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-bill-mobile"
                          >
                            เบอร์โทร
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-bill-mobile"
                            type="text"
                            name="billMobile"
                            value={form.billMobile}
                            onChange={(e) => {
                              const onlyNums = e.target.value.replace(
                                /[^0-9]/g,
                                "",
                              );
                              setForm((s) => ({ ...s, billMobile: onlyNums }));
                            }}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={10}
                          />
                        </FormGroup>
                      </Col>

                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-bill-email"
                          >
                            Email
                          </label>
                          <Input
                            className="form-control-alternative"
                            id="input-bill-email"
                            type="email"
                            name="billEmail"
                            value={form.billEmail}
                            onChange={onChange}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <ModalFooter>
        <Button color="warning" type="button" onClick={handleSubmit}>
          อัปเดต
        </Button>
      </ModalFooter>
    </>
  );
};

export default AdminOrganization;
